import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
requiredRefs = ['overflowMenu'];

#abortController = new AbortController();

connectedCallback() {
super.connectedCallback();

this.overflowMenu?.addEventListener(
'pointerleave',
() => this.#debouncedDeactivate(),
{
signal: this.#abortController.signal,
}
);

onDocumentLoaded(this.#preloadImages);
}

disconnectedCallback() {
super.disconnectedCallback();
this.#abortController.abort();
}

#state = {
activeItem: null,
};

get animationDelay() {
const value = this.dataset.animationDelay;
return value ? parseInt(value, 10) : 0;
}

get overflowMenu() {
return this.refs.overflowMenu?.shadowRoot?.querySelector(
'[part="overflow"]'
);
}

get overflowHovered() {
return this.refs.overflowMenu?.matches(':hover') ?? false;
}

activate = (event) => {
this.#debouncedDeactivate.cancel();
this.#debouncedActivateHandler.cancel();

this.#debouncedActivateHandler(event);
};

#activateHandler = (event) => {
this.#debouncedDeactivate.cancel();

if (!(event.target instanceof Element)) return;

const item = findMenuItem(event.target);

if (!item) return;

/*
* IMPORTANT:
* Keep normal desktop menu items from being switched into
* the overflow/full-menu state when they are clicked or focused.
*/
const isDefaultSlot = event.target.slot === '';

if (item === this.#state.activeItem) return;

this.dispatchEvent(new MegaMenuHoverEvent());

this.removeAttribute('data-animating');

const previouslyActiveItem = this.#state.activeItem;

if (previouslyActiveItem) {
previouslyActiveItem.ariaExpanded = 'false';
}

this.#state.activeItem = item;

this.ariaExpanded = 'true';
item.ariaExpanded = 'true';

/*
* Only use overflowExpanded for an actual overflow item.
* Normal items such as Flat Sheets must never trigger it.
*/
this.dataset.overflowExpanded = (!isDefaultSlot).toString();

let submenu = findSubmenu(item);
const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

if (!submenu && !isDefaultSlot) {
submenu = this.overflowMenu;
}

const submenuHeight = submenu
? Math.max(submenu.offsetHeight, overflowMenuHeight)
: 0;

this.style.setProperty('--submenu-height', `${submenuHeight}px`);
this.style.setProperty('--submenu-opacity', '1');
};

#debouncedActivateHandler = debounce(
this.#activateHandler,
ACTIVATE_DELAY
);

deactivate(event) {
this.#debouncedActivateHandler.cancel();

if (!(event.target instanceof Element)) return;

const item = findMenuItem(event.target);

if (item === this.#state.activeItem) {
this.#debouncedDeactivate();
}
}

#deactivate = (item = this.#state.activeItem) => {
if (!item || item !== this.#state.activeItem) return;

if (this.overflowHovered) return;

this.style.setProperty('--submenu-height', '0px');
this.style.setProperty('--submenu-opacity', '0');

this.dataset.overflowExpanded = 'false';

this.#state.activeItem = null;

this.ariaExpanded = 'false';
item.ariaExpanded = 'false';

item.setAttribute('data-animating', '');

setTimeout(() => {
item.removeAttribute('data-animating');
}, Math.max(0, this.animationDelay - 150));
};

#debouncedDeactivate = debounce(
this.#deactivate,
DEACTIVATE_DELAY
);

#preloadImages = () => {
const images = this.querySelectorAll('img[loading="lazy"]');

images?.forEach((image) => {
image.removeAttribute('loading');
});
};
}

if (!customElements.get('header-menu')) {
customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
if (!(element instanceof Element)) return null;

if (element.matches('[slot="more"]')) {
return findMenuItem(
element.parentElement?.querySelector('[slot="overflow"]')
);
}

return element.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
const submenu = element?.parentElement?.querySelector(
'[ref="submenu[]"]'
);

return submenu instanceof HTMLElement ? submenu : null;
}